// Every server call the screens need. Queries are cached and refreshed by
// socket events (see realtime/socket.ts); mutations invalidate what they touch.

import { useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { RadioDTO,
  FeatureFlag,
  AdReward,
  AutoDismantleRule,
  ClaimResultDTO,
  DismantleBulkResultDTO,
  CollectionDTO,
  ContractClaimDTO,
  ContractsDTO,
  EquipBestDTO,
  EventPromptDTO,
  GearSlot,
  BodyType,
  PlayerSearchResultDTO,
  Attributes,
  BuildingId,
  BuildingSitePutDTO,
  CampDTO,
  ClanDTO,
  ClanListItemDTO,
  CrewBoardsDTO,
  ClanRole,
  DropRatesDTO,
  Emblem,
  EncounterAction,
  EncounterPromptDTO,
  ExpeditionDTO,
  ExpeditionListItemDTO,
  ExpeditionPlanDTO,
  FeedItemDTO,
  FriendDTO,
  HordeDTO,
  InventoryDTO,
  MeDTO,
  NotifPrefs,
  NotificationDTO,
  OutfitRef,
  OutfitsDTO,
  PublicStatsDTO,
  SameOrdersDTO,
  SendExpeditionBody,
  SessionEndDTO,
  ShareCardDTO,
  SkillsDTO,
  StackKind,
  StackMap,
  SurvivorDTO,
  TraderBuyDTO,
  TraderDTO,
  WorkshopDTO,
  WorldDTO,
  ZoneId,
  SeasonClaimDTO,
  SeasonDTO,
  BlueprintUseDTO,
  RecalibrateResultDTO,
  LedgerDTO,
  LegacyDTO,
  SeasonEndDTO,
  SeasonScoreDTO,
  SupplyCrateDTO,
  RevengeTokenDTO,
  RenameDTO,
  LookMode,
} from '@lastday/shared';
import { api, ApiError } from './client';
import { liveMe, useLiveTick } from '@/lib/live';
import { toast } from '@/components/Toast';
import { t } from '@/i18n';
import { gainText, syncServerTime } from '@/lib/format';
import { ENERGY_RULES, lookOutfit, outfitName, unlockedIn, type StoryDTO } from '@lastday/shared';
import { sfx } from '@/lib/sound/sound';
import { clearOrderDrafts } from '@/lib/orderDraft';

export const qk = {
  me: ['me'] as const,
  survivor: ['survivor'] as const,
  inventory: ['inventory'] as const,
  equipBest: ['inventory', 'equip-best'] as const,
  camp: ['camp'] as const,
  workshop: ['workshop'] as const,
  world: ['world'] as const,
  plan: (zone: string) => ['plan', zone] as const,
  expedition: (id: string) => ['expedition', id] as const,
  expeditions: ['expeditions'] as const,
  encounter: (id: string) => ['encounter', id] as const,
  feed: (filter: string) => ['feed', filter] as const,
  horde: ['horde'] as const,
  friends: ['friends'] as const,
  clanMine: ['clan', 'mine'] as const,
  clans: ['clans'] as const,
  crewBoards: ['crew', 'boards'] as const,
  share: (code: string) => ['share', code] as const,
  notifications: ['notifications'] as const,
  publicStats: ['publicStats'] as const,
  outfits: ['outfits'] as const,
  skills: ['skills'] as const,
  trader: ['trader'] as const,
  season: ['season'] as const,
  // 4. 10. 2026 (package 3).
  seasonScore: ['season', 'score'] as const,
  seasonEnd: ['season', 'end'] as const,
  ledger: ['ledger'] as const,
};

// ---- Queries ---------------------------------------------------------------------

export const useMe = () => {
  // B1 (1. 10. 2026): energy and camp HP as they are now (lib/live.ts); the tick moves when their time comes.
  const tick = useLiveTick();
  return useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      // Timers follow the server clock (U3): measure the difference on every /me.
      const sentAt = Date.now();
      const me = await api.get<MeDTO>('/me');
      syncServerTime(me.serverTime, sentAt, Date.now());
      return me;
    },
    staleTime: 15_000,
    select: useCallback((me: MeDTO) => liveMe(me, tick), [tick]),
  });
};
export const useSurvivor = () => useQuery({ queryKey: qk.survivor, queryFn: () => api.get<SurvivorDTO>('/survivor') });
/**
 * 3. 10. 2026: what was opened in this session is no longer NEW, whatever an answer fetched before the server took the
 * "seen" says (a report or the inventory refetched while the request was on its way).
 */
const seenHere = { items: new Set<string>(), stacks: new Set<StackKind>() };
const inventorySeen = (d: InventoryDTO): InventoryDTO =>
  seenHere.items.size || seenHere.stacks.size
    ? { ...d, items: d.items.map((i) => (i.isNew && seenHere.items.has(i.id) ? { ...i, isNew: false } : i)), newStacks: d.newStacks.filter((k) => !seenHere.stacks.has(k)) }
    : d;
const expeditionSeen = (e: ExpeditionDTO): ExpeditionDTO =>
  seenHere.items.size && e.loot ? { ...e, loot: { ...e.loot, items: e.loot.items.map((i) => (i.isNew && seenHere.items.has(i.id) ? { ...i, isNew: false } : i)) } } : e;

export const useInventory = (enabled = true) => useQuery({ queryKey: qk.inventory, queryFn: async () => inventorySeen(await api.get<InventoryDTO>('/inventory')), enabled });
export const useCamp = () => useQuery({ queryKey: qk.camp, queryFn: () => api.get<CampDTO>('/camp'), refetchInterval: 60_000 });
export const useWorkshop = () => useQuery({ queryKey: qk.workshop, queryFn: () => api.get<WorkshopDTO>('/workshop') });
export const useWorld = () => useQuery({ queryKey: qk.world, queryFn: () => api.get<WorldDTO>('/world'), refetchInterval: 60_000 });
// 28. 10. 2026: the order sheet waits for a plan fetched after it opened (PrepareScreen); back within the 5 s a query stays
// fresh (Repair and straight back) it never refetched and stayed on Loading, so it refetches on every mount.
export const usePlan = (zone: ZoneId | undefined) =>
  useQuery({ queryKey: qk.plan(zone ?? ''), queryFn: () => api.get<ExpeditionPlanDTO>(`/expeditions/plan/${zone}`), enabled: !!zone, refetchOnMount: 'always' });
/** After sending a survivor (6.8): the reasons to come back. */
export const useSessionEnd = (id: string | undefined) => useQuery({ queryKey: ['session-end', id], queryFn: () => api.get<SessionEndDTO>(`/expeditions/${id}/session-end`), enabled: !!id, staleTime: 30_000 });
export const useExpedition = (id: string | undefined, opts: { poll?: boolean } = {}) =>
  useQuery({ queryKey: qk.expedition(id ?? ''), queryFn: async () => expeditionSeen(await api.get<ExpeditionDTO>(`/expeditions/${id}`)), enabled: !!id, refetchInterval: opts.poll ? 15_000 : false });
export const useExpeditions = () => useQuery({ queryKey: qk.expeditions, queryFn: () => api.get<ExpeditionListItemDTO[]>('/expeditions') });
/** Package 4, part E (6): a revenge token and where its target is now. */
export const useRevenge = (id: string | undefined) =>
  useQuery({ queryKey: ['revenge', id ?? ''], queryFn: () => api.get<RevengeTokenDTO>(`/revenge/${id}`), enabled: !!id, staleTime: 0, retry: false });
export const useEncounter = (id: string | undefined) =>
  useQuery({ queryKey: qk.encounter(id ?? ''), queryFn: () => api.get<EncounterPromptDTO>(`/encounters/${id}`), enabled: !!id });
/** 20. 10. 2026 (owner): `following`, the notes about the survivors the player follows. */
export const useFeed = (filter: 'all' | 'rumors' | 'events' | 'friends' | 'following') =>
  useQuery({ queryKey: qk.feed(filter), queryFn: () => api.get<{ items: FeedItemDTO[] }>(`/feed?filter=${filter}`).then((r) => r.items) });
/** A system behind a feature flag (krug 2, G) is on; true until /api/me says otherwise. */
export function useFeature(flag: FeatureFlag): boolean {
  return useMe().data?.features[flag] !== false;
}

export const useHorde = () => {
  const on = useFeature('horde');
  return useQuery({ queryKey: qk.horde, queryFn: () => api.get<HordeDTO | { none: true }>('/horde').then((r) => ('none' in r ? null : r)), refetchInterval: 20_000, enabled: on });
};
export const useFriends = () => useQuery({ queryKey: qk.friends, queryFn: () => api.get<FriendDTO[]>('/friends') });
export const useMyClan = () => useQuery({ queryKey: qk.clanMine, queryFn: () => api.get<{ clan: ClanDTO | null }>('/clans/mine').then((r) => r.clan) });
export const useClans = () => useQuery({ queryKey: qk.clans, queryFn: () => api.get<ClanListItemDTO[]>('/clans') });
/** 11. 10. 2026 (BACKLOG #33): the crews' leaderboards (the horde, the season). */
export const useCrewBoards = (enabled = true) => useQuery({ queryKey: qk.crewBoards, queryFn: () => api.get<CrewBoardsDTO>('/crews/boards'), enabled, staleTime: 60_000 });
export const useShareCard = (code: string | undefined) =>
  useQuery({ queryKey: qk.share(code ?? ''), queryFn: () => api.get<ShareCardDTO>(`/share/${code}`), enabled: !!code, retry: false });
export const useNotifications = () => useQuery({ queryKey: qk.notifications, queryFn: () => api.get<{ items: NotificationDTO[] }>('/notifications').then((r) => r.items) });
export const usePublicStats = () => useQuery({ queryKey: qk.publicStats, queryFn: () => api.get<PublicStatsDTO>('/public/stats'), refetchInterval: 30_000 });

// ---- Mutations -------------------------------------------------------------------

/** Wraps a server action: invalidates the given keys, shows errors as toasts. */
/** `optimistic` (20. 10. 2026): changes the cache before the server answers; a refused call refetches what it touched. */
export function useAction<TArgs, TRes = unknown>(
  fn: (args: TArgs) => Promise<TRes>,
  invalidate: QueryKey[] = [],
  opts: { success?: string | ((r: TRes) => string); silent?: boolean; optimistic?: (qc: QueryClient, args: TArgs) => Promise<void> | void } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onMutate: opts.optimistic ? (args: TArgs) => opts.optimistic!(qc, args) : undefined,
    onSuccess: (r) => {
      for (const k of invalidate) qc.invalidateQueries({ queryKey: k });
      const msg = typeof opts.success === 'function' ? opts.success(r) : opts.success;
      if (msg) toast.info(msg);
    },
    onError: (e) => {
      if (opts.optimistic) for (const k of invalidate) qc.invalidateQueries({ queryKey: k });
      if (!opts.silent) toast.error(e instanceof ApiError ? e.message : 'Could not reach the server');
    },
  });
}

type Worn = SurvivorDTO['equipment'];

/**
 * 20. 10. 2026 (owner): an equip shows at once (the worn gear next to the portrait, the slots and, with Match my gear, the
 * portrait itself) before the server answers; the refetch after the answer brings the server's truth.
 */
async function wearNow(qc: QueryClient, change: (worn: Worn, items: InventoryDTO['items']) => Worn | null) {
  await qc.cancelQueries({ queryKey: qk.survivor });
  const items = qc.getQueryData<InventoryDTO>(qk.inventory)?.items ?? [];
  let outfit: SurvivorDTO['outfit'] | undefined;
  qc.setQueryData<SurvivorDTO>(qk.survivor, (s) => {
    const equipment = s ? change(s.equipment, items) : null;
    if (!s || !equipment) return s;
    outfit = s.lookMode === 'gear' ? lookOutfit({ look: 'gear' }, equipment, unlockedIn(s.unlockedOutfits)) : s.outfit;
    return { ...s, equipment, outfit };
  });
  if (outfit !== undefined) qc.setQueryData<MeDTO>(qk.me, (m) => (m?.survivor ? { ...m, survivor: { ...m.survivor, outfit: outfit! } } : m));
}

/** The worn gear after `slot` takes the item `id` from the inventory (null: the slot is emptied; an item not in the cache: as it was). */
function putOn(worn: Worn, items: InventoryDTO['items'], slot: GearSlot, id: string | null): Worn {
  const next = { ...worn };
  if (!id) delete next[slot];
  else {
    const it = items.find((i) => i.id === id);
    if (it) next[slot] = { ...it, location: 'equipped' };
  }
  return next;
}

const ALL: QueryKey[] = [qk.me, qk.survivor, qk.camp, qk.inventory];

export const useGuestLogin = () => useAction(() => api.post('/auth/guest'), [qk.me], { silent: true });
export const useLogout = () => useAction(() => api.post('/auth/logout'), [qk.me]);
/** Feedback button (krug 3, U5). */
export const useSendFeedback = () => useAction((b: { text: string; page: string; screenshot: string | null }) => api.post<{ ok: boolean; id: string }>('/feedback', b));
/** "Save your survivor" card on Camp (docs/onboarding.md, section 6). */
export const useLinkPrompt = () => useAction((action: 'shown' | 'accepted' | 'dismissed') => api.post('/link-prompt', { action }), [qk.camp], { silent: true });
export const useEmailStart = () => useAction((email: string) => api.post<{ ok: boolean; devLink: string | null }>('/auth/email/start', { email }));
export const useTelegramLogin = () => useAction((body: { initData?: string; widget?: Record<string, string> }) => api.post('/auth/telegram', body), [qk.me]);

export const useAllocate = () => useAction((add: Partial<Attributes>) => api.post('/survivor/attributes', { add }), [qk.me, qk.survivor, qk.equipBest]);
export const useRespec = () => useAction(() => api.post('/survivor/respec'), [qk.me, qk.survivor, qk.camp]);
/** Survivors from before section 8 pick their look once (8.1). */
export const useSetBodyType = () => useAction((bodyType: BodyType) => api.post('/survivor/body-type', { bodyType }), [qk.survivor, qk.me]);
export const useSetTitle = () => useAction((title: string | null) => api.post('/survivor/title', { title }), [qk.survivor, qk.me]);
/** Krug 5 (K10): the skill tree. Apply sends the pending nodes together; the answer is the new tree. */
export const useSkills = () => useQuery({ queryKey: qk.skills, queryFn: () => api.get<SkillsDTO>('/skills') });
export function useApplySkills() {
  const qc = useQueryClient();
  return useAction(
    async (nodes: string[]) => {
      const r = await api.post<SkillsDTO>('/skills/apply', { nodes });
      qc.setQueryData(qk.skills, r);
      return r;
    },
    [qk.survivor, qk.me],
  );
}
export function useResetSkills() {
  const qc = useQueryClient();
  return useAction(
    async () => {
      const r = await api.post<SkillsDTO>('/skills/reset');
      qc.setQueryData(qk.skills, r);
      return r;
    },
    [qk.survivor, qk.me, qk.inventory],
  );
}
/** The first-visit intro was seen (K10.7, kept in users.flags). */
export const useSkillIntro = () => useAction(() => api.post('/skills/intro'), [qk.skills], { silent: true });
/** K10.9: the Skills screen was opened, or a path to a keystone looked at (analytics, never fails the UI). */
export const skillEvent = (b: { event: 'open' | 'preview'; node?: string; device: 'desktop' | 'mobile' }) => {
  api.post('/skills/event', b).catch(() => undefined);
};

export const useEquip = () =>
  useAction((id: string) => api.post(`/items/${id}/equip`), [qk.inventory, qk.survivor, qk.me, qk.camp], {
    optimistic: (qc, id) =>
      wearNow(qc, (worn, items) => {
        const it = items.find((i) => i.id === id);
        return it ? putOn(worn, items, it.slot, id) : null;
      }),
  });
export const useUnequip = () =>
  useAction((id: string) => api.post(`/items/${id}/unequip`), [qk.inventory, qk.survivor, qk.me], {
    optimistic: (qc, id) =>
      wearNow(qc, (worn, items) => {
        const slot = (Object.keys(worn) as GearSlot[]).find((k) => worn[k]?.id === id);
        return slot ? putOn(worn, items, slot, null) : null;
      }),
  });
export const useRepair = () => useAction((p: { id: string; kit?: 'repair_kit' | 'precision_repair_kit' }) => api.post(`/items/${p.id}/repair`, { kit: p.kit ?? false }), [qk.inventory, qk.survivor, qk.me], { success: 'Repaired' });
/** 7. 10. 2026 (owner): a broken item goes to the Workshop (a job on one bench). */
export const useWorkshopRepair = () => useAction((id: string) => api.post(`/items/${id}/workshop-repair`, {}), [qk.inventory, qk.survivor, qk.me, qk.workshop, qk.camp], { success: 'Repair started in the Workshop' });
/** Item upgrade +N (krug 5, F9): a job in the Workshop queue. */
export const useUpgradeItem = () =>
  useAction((p: { id: string; affix?: number }) => api.post(`/items/${p.id}/upgrade`, p.affix != null ? { affix: p.affix } : {}), [qk.inventory, qk.camp, qk.workshop], { success: 'Upgrade started in the Workshop' });
/** 1. 10. 2026 (C4): a tier upgrade, a job in the Workshop queue. */
export const useTierUp = () => useAction((id: string) => api.post(`/items/${id}/tierup`), [qk.inventory, qk.camp, qk.workshop], { success: 'Tier upgrade started in the Workshop' });
export const useDismantle = () =>
  useAction((id: string) => api.post<{ scrap: number; extra: StackMap }>(`/items/${id}/dismantle`), [qk.inventory, qk.me], { success: (r) => `Dismantled: ${gainText(r.scrap, r.extra)}` });
/** Krug 5 (K7.1): many items at once (the sheet shows its own toast with what came back). */
export const useDismantleBulk = () => useAction((itemIds: string[]) => api.post<DismantleBulkResultDTO>('/items/dismantle-bulk', { itemIds }), [qk.inventory, qk.me, qk.camp, qk.survivor], { silent: true });
export const useCollection = () => useQuery({ queryKey: ['collection'], queryFn: () => api.get<CollectionDTO>('/collection') });
/** Package 4, part I: the season's story chains; a Station 9 step (the radio, a delivery). */
export const useStory = () => useQuery({ queryKey: ['story'], queryFn: () => api.get<StoryDTO>('/story') });
/** Package 4, part L: Danny Dawn's radio, and listening to the latest broadcast. */
export const useRadio = () => useQuery({ queryKey: ['radio'], queryFn: () => api.get<RadioDTO>('/radio'), refetchInterval: 10 * 60_000 });
export const useRadioListen = () => useAction(() => api.post<RadioDTO>('/radio/listen'), [['radio'], qk.me], { silent: true });
/** 8. 10. 2026 (owner): the season's finale, Last Broadcast: Electronics from Storage for Danny's relay. */
export const useFinaleGive = () => useAction((amount: number) => api.post<RadioDTO>('/radio/finale', { amount }), [['radio'], qk.inventory, qk.me], { success: 'Danny thanks you. The relay hums a little louder.' });
/** Package 4, part L: a reward claimed plays its sound. */
const claimed = <T,>(r: T): T => {
  sfx('goal_claimed');
  return r;
};
export const useStoryAct = () => useAction((chain: string) => api.post<StoryDTO>(`/story/${chain}/act`), [['story'], qk.inventory, qk.me], { success: 'Done. The story goes on.' });
/** The wardrobe (docs/OUTFITI_garderoba.md, 4.2): every outfit with its five pieces. Not refetched on focus (each load is one `appearance_opened`). */
export const useOutfits = () => useQuery({ queryKey: qk.outfits, queryFn: () => api.get<OutfitsDTO>('/survivor/outfits'), refetchOnWindowFocus: false });
/** Wear an unlocked outfit, or null for the base look (4.4); 20. 10. 2026 (owner): `'match'` is Match my gear. */
export const useSelectOutfit = () => {
  const qc = useQueryClient();
  return useAction(
    async (o: OutfitRef | null | 'match') => {
      const r = await api.post<{ outfit: OutfitRef | null; mode: LookMode }>('/survivor/outfit', o === 'match' ? { match: true } : (o ?? { outfit: null }));
      qc.setQueryData<OutfitsDTO>(qk.outfits, (d) => (d ? { ...d, current: r.outfit, mode: r.mode } : d));
      return r;
    },
    [qk.survivor, qk.me],
    { success: (r) => (r.mode === 'gear' ? t('appearance.match.toast') : r.outfit ? t('appearance.woreToast', { name: outfitName(r.outfit) }) : t('appearance.baseToast')) },
  );
};
/** 20. 10. 2026 (owner): a new name for the survivor (the sheet shows the server's reason when it is refused). */
export const useRename = () => useAction((name: string) => api.post<{ name: string; rename: RenameDTO }>('/survivor/name', { name }), [qk.survivor, qk.me], { silent: true });
/** Open choice event of a run (6.1). */
export const useEventPrompt = (expeditionId: string | null) =>
  useQuery({ queryKey: ['event', expeditionId], queryFn: () => api.get<{ prompt: EventPromptDTO | null }>(`/expeditions/${expeditionId}/event`), enabled: !!expeditionId, staleTime: 0 });
export const useChooseEvent = () =>
  useAction((p: { expeditionId: string; optionId: string }) => api.post(`/expeditions/${p.expeditionId}/event`, { optionId: p.optionId }), [qk.me, ['event'], ['expedition']]);
/** Contract board; sends the player's time zone so the daily reset is at their midnight. */
export const useContracts = () => useQuery({ queryKey: ['contracts'], queryFn: () => api.get<ContractsDTO>(`/contracts?tz=${new Date().getTimezoneOffset()}`) });
export const useClaimContract = () =>
  useAction((id: string) => api.post<ContractClaimDTO>(`/contracts/${id}/claim`).then(claimed), [['contracts'], qk.me, qk.survivor, qk.inventory, qk.camp], {
    success: (r) => [`+${r.xp} XP`, ...Object.entries(r.stacks).map(([k, v]) => `${v} ${k.replace('_', ' ')}`), r.crate, r.bonus && t('camp.contracts.bonusToast', { name: r.bonus, n: r.bonusEnergy })].filter(Boolean).join(' · '),
  });
export const useSwapContract = () => useAction((id: string) => api.post<ContractsDTO>(`/contracts/${id}/swap`), [['contracts']]);
export const useLockItem = () => useAction((p: { id: string; locked: boolean }) => api.post(`/items/${p.id}/lock`, { locked: p.locked }), [qk.inventory]);
/** Package 4, part E: lock worn slots against a winner (the Barricade sets how many). */
export const useSlotLocks = () => useAction((slots: string[]) => api.post('/survivor/slot-locks', { slots }), [qk.survivor, qk.me]);
export const useAutoDismantle = () => useAction((rule: AutoDismantleRule) => api.post('/inventory/auto-dismantle', rule), [qk.inventory], { success: 'Auto-dismantle saved' });
/** Camp quick fixes while Storage is full (krug 5, K1). */
export const useEnableAutoDismantle = () => useAction(() => api.post('/inventory/auto-dismantle/enable'), [qk.inventory, qk.camp], { success: 'Auto-dismantle on' });
/** Krug 5 (K6.2): the Camp's offer to dismantle Common gear automatically (the card shows its own toast). */
export const useAutoDismantlePrompt = () =>
  useAction((p: { action: 'shown' | 'accepted' | 'dismissed'; dismantleNow?: boolean }) => api.post<{ count: number; scrap: number; extra: StackMap }>('/auto-dismantle-prompt', p), [qk.camp, qk.inventory, qk.me], { silent: true });
export const useDismantleCommon = () =>
  useAction(() => api.post<{ count: number; scrap: number; extra: StackMap }>('/inventory/dismantle-common'), [qk.inventory, qk.camp, qk.me], {
    success: (r) => `Dismantled ${r.count} item${r.count === 1 ? '' : 's'}: ${gainText(r.scrap, r.extra)}`,
  });
/** The "Equip best" plan; under the inventory key, so every gear change refreshes it (B1). */
export const useEquipBestPreview = (enabled: boolean) => useQuery({ queryKey: qk.equipBest, queryFn: () => api.get<EquipBestDTO>('/equip-best'), enabled, staleTime: 0 });
export const useEquipBest = () =>
  useAction((changes: { slot: GearSlot; to: string | null }[]) => api.post('/equip-best', { changes }), [qk.inventory, qk.survivor, qk.me, qk.camp], {
    success: 'Best gear on',
    // 20. 10. 2026 (owner): the worn gear and the matched look show at once.
    optimistic: (qc, changes) => wearNow(qc, (worn, items) => changes.reduce((w, c) => putOn(w, items, c.slot, c.to), worn)),
  });
/**
 * 3. 10. 2026: NEW means "not looked at yet". Opening an item's or a stack's detail clears it for good: at once in the
 * cached inventory (cards, tabs) and in cached reports (loot tiles, "N new"), then on the server.
 */
export function useMarkSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { ids?: string[] | 'all'; stacks?: StackKind[] }) => api.post('/items/seen', b),
    onMutate: (b) => {
      const all = b.ids === 'all';
      const ids = new Set(all ? [] : (b.ids ?? []));
      const stacks = new Set(b.stacks ?? []);
      for (const id of ids) seenHere.items.add(id);
      for (const k of stacks) seenHere.stacks.add(k);
      const seen = (id: string) => all || ids.has(id);
      qc.setQueryData<InventoryDTO>(qk.inventory, (d) =>
        d ? { ...d, items: d.items.map((i) => (i.isNew && seen(i.id) ? { ...i, isNew: false } : i)), newStacks: all ? [] : d.newStacks.filter((k) => !stacks.has(k)) } : d,
      );
      qc.setQueriesData<ExpeditionDTO>({ queryKey: ['expedition'] }, (e) =>
        e && 'loot' in e && e.loot ? { ...e, loot: { ...e.loot, items: e.loot.items.map((i) => (i.isNew && seen(i.id) ? { ...i, isNew: false } : i)) } } : e,
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.inventory });
      qc.invalidateQueries({ queryKey: ['expedition'] });
    },
  });
}
export const useUseStack = () => useAction((kind: StackKind) => api.post<{ healed?: number; discovered?: ZoneId; first?: boolean; shareCode?: string | null; cache?: boolean }>('/stacks/use', { kind }), [...ALL, qk.world]);
export const useUnloadPack = () => useAction(() => api.post('/pack/unload'), [qk.inventory, qk.me]);
/** Krug 5 (K4.4): throw part of a Storage stack away. */
export const useDiscardStack = () => useAction((p: { kind: StackKind; qty: number }) => api.post<{ kind: StackKind; discarded: number; left: number }>('/stacks/discard', p), [qk.inventory, qk.me, qk.camp]);

export const useUpgradeBuilding = () => useAction((id: BuildingId) => api.post(`/buildings/${id}/upgrade`), [qk.camp, qk.me, qk.workshop, qk.inventory]);
/** 23. 10. 2026 (owner, a player's feedback): everything Storage has toward a building's next level (the building site). */
export const useBuildingSite = () => useAction((id: BuildingId) => api.post<BuildingSitePutDTO>(`/buildings/${id}/site`), [qk.camp, qk.me, qk.workshop, qk.inventory, qk.trader, ['contracts']]);
export const useClaimDaily = () => useAction(() => api.post<{ day: number; label: string; item: string | null }>('/daily/claim').then(claimed), ALL, { success: (r) => `Day ${r.day}: ${r.item ?? r.label}` });
export const useClaimAd = () =>
  useAction((p: { reward: AdReward; expeditionId?: string | null }) => api.post('/ads/claim', p), [...ALL, ['expedition'], qk.trader]);

// ---- Trader (krug 5, K11) ------------------------------------------------------------------

/**
 * The day's offers, the refreshes and the Sell tab. `opened`: this is the Trader's screen, so the
 * first load counts as opening him (`trader_open`); later refetches do not.
 */
export function useTrader(enabled = true, opened = false) {
  const first = useRef(opened);
  return useQuery({
    queryKey: qk.trader,
    queryFn: () => {
      const open = first.current;
      first.current = false;
      return api.get<TraderDTO>(`/trader${open ? '?open=1' : ''}`);
    },
    enabled,
    // His screen loads fresh even when the Camp's "Trade at Tobias" link fetched him a moment ago.
    refetchOnMount: opened ? 'always' : true,
  });
}
const TRADE: QueryKey[] = [qk.trader, qk.me, qk.survivor, qk.inventory, qk.camp];
/** A purchase or an exchange; `key` makes a repeated click count once. */
export const useTraderBuy = () => useAction((p: { offerId: string; qty?: number; key: string }) => api.post<TraderBuyDTO>('/trader/buy', p), TRADE);
export const useTraderSell = () => useAction((p: { resource: StackKind; qty: number }) => api.post<{ resource: StackKind; qty: number; scrap: number; used: number; limit: number }>('/trader/sell', p), TRADE);
/** The free refresh of the day (the one with an ad goes through the ad modal and `/ads/claim`). */
export function useTraderRefresh() {
  const qc = useQueryClient();
  return useAction(
    async () => {
      const r = await api.post<TraderDTO>('/trader/refresh', { via: 'free' });
      qc.setQueryData(qk.trader, r);
      return r;
    },
    [qk.camp],
  );
}

// ---- The season path (krug 5, K15.2) -------------------------------------------------------------

export const useSeason = () => useQuery({ queryKey: qk.season, queryFn: () => api.get<SeasonDTO>('/season') });
/** Claims one goal's reward; the toast names what came (the box's item by name). */
export const useSeasonClaim = () =>
  useAction((goal: number) => api.post<SeasonClaimDTO>('/season/claim', { goal }).then(claimed), [qk.season, ...ALL], {
    success: (r) => t('season.claimedToast', { what: r.items.length ? `${r.reward}: ${r.items.join(', ')}` : r.reward }),
  });

/** 2. 10. 2026 (package 1, 5.b): `keepSearching` queues up to 1 + the Barricade level loops. */
/** 28. 10. 2026 (owner, fb-balance): `skip`, what the scouts leave out there (the server remembers it per place). */
export const useSendScout = () => useAction((p: { zone: ZoneId; keepSearching: boolean; skip: StackKind[] }) => api.post('/scout', p), [qk.camp]);

export const useCraft = () => useAction((b: { recipeId: string; count: number; batch?: number }) => api.post('/workshop/craft', b), [qk.workshop, qk.camp, qk.me]);
/** 6. 10. 2026 (Workshop 9): `batch` cancels every piece of a batch; a piece's id alone cancels that piece. */
export const useCancelCraft = () => useAction((p: { id: string; batch?: boolean }) => api.post(`/workshop/jobs/${p.id}/cancel`, p.batch ? { batch: true } : {}), [qk.workshop, qk.camp, qk.inventory]);

// ---- Package 3 (4. 10. 2026) ------------------------------------------------------------------------
/** J2: FORGE with three slots (the shards are paid, three candidates saved), then CHOOSE one. */
export const useRelicRoll = () => useAction((slots: string[]) => api.post('/workshop/relic/roll', { slots }), [qk.workshop, qk.camp, qk.me]);
export const useRelicChoose = () => useAction((itemId: string) => api.post('/workshop/relic/choose', { itemId }), [qk.workshop, qk.camp]);
/** I: using a Blueprint (the item picked from its offer; none when it only turns into materials). */
export const useBlueprintUse = () => useAction((p: { id: string; templateId: string | null }) => api.post<BlueprintUseDTO>(`/blueprints/${p.id}/use`, { templateId: p.templateId }), [qk.inventory, qk.workshop, qk.camp]);
/** H: Rework with its donors (a job in the Workshop queue). */
export const useRework = () => useAction((p: { id: string; donors: string[] }) => api.post(`/items/${p.id}/rework`, { donors: p.donors }), [qk.inventory, qk.workshop, qk.camp], { success: 'Rework started in the Workshop' });
/** 11. 10. 2026 (BACKLOG #36): Recalibrate one affix, right away (the answer has the new affix). */
export const useRecalibrate = () =>
  useAction((p: { id: string; affix: number }) => api.post<RecalibrateResultDTO>(`/items/${p.id}/recalibrate`, { affix: p.affix }), [qk.inventory, qk.workshop, qk.camp, qk.survivor]);
/** G: the Veteran Ledger. */
export const useLedger = () => useQuery({ queryKey: qk.ledger, queryFn: () => api.get<LedgerDTO>('/ledger') });
export function useLedgerBuy() {
  const qc = useQueryClient();
  return useAction(
    async (node: string) => {
      const r = await api.post<LedgerDTO>(`/ledger/${node}/buy`);
      qc.setQueryData(qk.ledger, r);
      return r;
    },
    [qk.camp, qk.inventory, qk.me],
  );
}
/** B, C, D: the Season Score tab, the Season end tab and the choices after a season's end. */
export const useSeasonScore = () => useQuery({ queryKey: qk.seasonScore, queryFn: () => api.get<SeasonScoreDTO>('/season/score') });
export const useSeasonEnd = () => useQuery({ queryKey: qk.seasonEnd, queryFn: () => api.get<SeasonEndDTO>('/season/end') });
export const useLegacyBuy = () => useAction((id: string) => api.post<LegacyDTO>(`/legacy/${id}/buy`), [qk.seasonEnd, qk.me]);
export const useSeasonOutfit = () => useAction((set: string) => api.post('/season/end/outfit', { set }), [qk.seasonEnd, qk.me, qk.outfits]);
export const useKeepRecipes = () => useAction((templateIds: string[]) => api.post('/season/end/recipes', { templateIds }), [qk.seasonEnd, qk.workshop, qk.me]);
export const useWalkOut = () => useAction((background: string) => api.post('/season/end/walk-out', { background }), [qk.seasonEnd, ...ALL]);
/** E: a Supply crate after the last goal. */
export const useSupplyCrate = () => useAction(() => api.post<SupplyCrateDTO>('/season/crate'), [qk.season, qk.inventory, qk.camp]);

/** 28. 10. 2026: a run sent (any way) takes the order sheet's drafts away (lib/orderDraft.ts). */
function sentRun<T>(r: T): T {
  clearOrderDrafts();
  return r;
}
export const useSendExpedition = () => useAction((b: SendExpeditionBody) => api.post<ExpeditionDTO>('/expeditions', b).then(sentRun), [...ALL, qk.world, qk.expeditions]);
/** 2. 10. 2026 (package 1, 5.c): what "Same orders" would send now (the zone of the last run, or this one). */
export const useSameOrders = (zone: ZoneId | null | undefined, enabled = true) =>
  useQuery({ queryKey: ['same-orders', zone ?? ''], queryFn: () => api.get<{ sameOrders: SameOrdersDTO | null }>(`/expeditions/same-orders${zone ? `?zone=${zone}` : ''}`).then((r) => r.sameOrders), enabled });
/** Sends the remembered orders again in one tap (5.c). */
export const useSendSameOrders = () => useAction((zone: ZoneId | null) => api.post<ExpeditionDTO>('/expeditions/same-orders', zone ? { zone } : {}).then(sentRun), [...ALL, qk.world, qk.expeditions]);
export const useClaimExpedition = () =>
  useAction((id: string) => api.post<ClaimResultDTO>(`/expeditions/${id}/claim`), [...ALL], {
    silent: true,
    success: (r) => (r.autoDismantled?.count ? `Auto-dismantled ${r.autoDismantled.count} item${r.autoDismantled.count === 1 ? '' : 's'}: ${gainText(r.autoDismantled.scrap, r.autoDismantled.extra)}` : ''),
  });
export const useRecall = () => useAction((id: string) => api.post(`/expeditions/${id}/recall`), [qk.me], { success: 'Your survivor is heading home' });
export const useFinishNow = () => useAction((id: string) => api.post(`/expeditions/${id}/finish-now`), [...ALL, qk.expeditions], { success: 'Your survivor is back' });
export const useDecide = () => useAction((p: { id: string; action: EncounterAction }) => api.post<{ ok: boolean; resolved: boolean }>(`/encounters/${p.id}/decide`, { action: p.action }), [qk.me]);

/** Package 4, part O: `ration`: eat a Horde Ration for tonight's horde. 10. 10. 2026 (BACKLOG #41): the rounds and Bandages taken. */
export const useJoinHorde = () => useAction((p?: { ration?: boolean; feed?: number; bandages?: number }) => api.post<{ id: string }>('/horde/join', p ?? {}), [qk.horde, qk.me, qk.inventory]);
export const useClaimHorde = () => useAction(() => api.post<{ rewards: { label: string }[] }>('/horde/claim').then(claimed), [qk.horde, ...ALL], { success: (r) => r.rewards.map((x) => x.label).join(' · ') });
/** Package 4, F4: Stand guard: the total of prepaid waves wanted for tonight (one energy each, spent now). */
export const useStandGuard = () =>
  useAction((p: { waves: number; ration?: boolean; feed?: number; bandages?: number }) => api.post<{ waves: number }>('/horde/guard', p), [qk.horde, qk.me, qk.inventory], { success: (r) => (r.waves === 0 ? 'Off guard tonight' : `On guard tonight: ${r.waves} ${r.waves === 1 ? 'wave' : 'waves'}`) });
/** 7. 10. 2026 (owner): fire barrels in the hour before the horde, and the generator at Station 9. */
export const useLightBarrels = () => useAction(() => api.post<{ ok: true }>('/horde/barrels'), [...ALL, qk.horde], { success: 'The barrels are burning.' });
export const useSetGenerator = () => useAction((on: boolean) => api.post<{ on: boolean; running: boolean }>('/camp/generator', { on }), ALL);

/** A player picked from a search (userId) or typed in (invite code, or a name only one survivor has). */
export type PlayerRef = { userId: string } | { name: string };
export const useAddFriend = () => useAction((ref: PlayerRef) => api.post<{ status: string }>('/friends/add', ref), [qk.friends], { success: (r) => (r.status === 'accepted' ? 'You are now friends' : 'Request sent') });
/** Names are not unique (8.1): a search returns everyone who fits. */
export const usePlayerSearch = (q: string) =>
  useQuery({ queryKey: ['players', q.trim().toLowerCase()], queryFn: () => api.get<PlayerSearchResultDTO[]>(`/players/search?q=${encodeURIComponent(q.trim())}`), enabled: q.trim().length >= 2, staleTime: 30_000 });
export const useFriendAction = () =>
  useAction((p: { uid: string; action: 'accept' | 'remove' }) => api.post(`/friends/${p.uid}/${p.action}`), [qk.friends], { success: 'Done' });
/** 28. 10. 2026 (player report): an energy gift says what it gave and to whom (it said "Done"). */
export const useGiftEnergy = () =>
  useAction((f: { uid: string; name: string }) => api.post(`/friends/${f.uid}/gift`).then(() => f), [qk.friends], { success: (f) => t('settings.friends.giftDone', { name: f.name, n: ENERGY_RULES.friendGift }) });

export const useCreateClan = () => useAction((b: { name: string; tag: string; description: string; open: boolean }) => api.post<{ id: string }>('/clans', b), [qk.clanMine, qk.me, qk.clans]);
export const useJoinClan = () => useAction((id: string) => api.post(`/clans/${id}/join`), [qk.clanMine, qk.me]);
export const useLeaveClan = () => useAction(() => api.post('/clans/leave'), [qk.clanMine, qk.me, qk.clans]);
export const useClanRole = () => useAction((p: { userId: string; role: ClanRole | 'kick' }) => api.post('/clans/role', p), [qk.clanMine]);
export const useClanEmblem = () => useAction((e: Emblem) => api.post('/clans/emblem', e), [qk.clanMine], { success: 'Emblem saved' });
// 11. 10. 2026 (owner, BACKLOG #33): crews. A donation is crew XP; applications, settings, chests, sends, the rally, the chat's report and block.
export const useDonate = () => useAction((stacks: StackMap) => api.post<{ xp: number; leftXp: number }>('/clans/donate', { stacks }), [qk.clanMine, qk.me, qk.inventory]);
export const useClanChat = () => useAction((body: string) => api.post<{ masked: number }>('/clans/chat', { body }), [qk.clanMine]);
export const useCrewApply = () => useAction((id: string) => api.post<{ joined: boolean }>(`/clans/${id}/apply`), [qk.clanMine, qk.me, qk.clans], { success: (r) => (r.joined ? 'You joined the crew' : 'Application sent') });
export const useAnswerApplication = () => useAction((p: { userId: string; accept: boolean }) => api.post('/clans/applications', p), [qk.clanMine]);
export const useCrewSettings = () => useAction((p: { description?: string; open?: boolean }) => api.post('/clans/settings', p), [qk.clanMine], { success: 'Saved' });
export const useOpenChest = () => useAction((p: { season: number; level: number }) => api.post<{ got: string[] }>('/clans/chest', p), [qk.clanMine, ...ALL], { success: (r) => r.got.join(' · ') });
export const useCrewSend = () => useAction((p: { userId: string; stacks: StackMap }) => api.post<{ value: number }>('/clans/send', p), [qk.clanMine, qk.me, qk.inventory]);
export const useCrewRally = () => useAction(() => api.post<{ text: string }>('/clans/rally'), [qk.clanMine], { success: (r) => r.text });
export const useReportChat = () => useAction((p: { id: string; reason: string }) => api.post(`/clans/chat/${p.id}/report`, { reason: p.reason }), [], { success: 'Reported. Thank you.' });
// 23. 10. 2026: a blocked player's messages leave the unread count too (/me).
export const useBlockChat = () => useAction((p: { userId: string; block: boolean }) => api.post<{ blocked: string[] }>('/clans/block', p), [qk.clanMine, qk.me]);
export const useClanInvite = () => useAction((ref: PlayerRef) => api.post<{ invited: string }>('/clans/invite', ref), [qk.clanMine], { success: (r) => `${r.invited} was invited` });
export const useStartRaid = () => useAction((zone: ZoneId) => api.post<{ id: string }>('/clans/raid', { zone }), [qk.clanMine, qk.world]);
export const useSendRaid = () => useAction(() => api.post<{ id: string }>('/raid/send').then(sentRun), [...ALL, qk.clanMine]);

export const useReadNotifications = () => useAction(() => api.post('/notifications/read'), [qk.notifications, qk.me], { silent: true });
export const useNotifPrefs = () => useAction((p: NotifPrefs) => api.post('/settings/notif', p), [qk.me], { success: 'Saved' });
export const useNewsletter = () => useAction((email: string) => api.post('/public/newsletter', { email }));

// Dev tools (only work when the server runs with DEV_TOOLS=1)
export const useDevDropRates = (enabled: boolean) => useQuery({ queryKey: ['dev', 'drop-rates'], queryFn: () => api.get<DropRatesDTO>('/dev/drop-rates'), enabled, staleTime: 0 });
export const useDev = () =>
  useAction((p: { path: 'finish' | 'grant' | 'rush' | 'horde'; body?: unknown }) => api.post(`/dev/${p.path}`, p.body ?? {}), [...ALL, qk.world, qk.horde, qk.workshop, ['expedition']], { success: 'Done' });
